import QRCodeStyling from "qr-code-styling";
import jsQR from "jsqr";

// ---------- Contrast (WCAG) ----------
function hexToRgb(hex) {
  const h = hex.replace("#", "");
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  return {
    r: parseInt(full.substring(0, 2), 16),
    g: parseInt(full.substring(2, 4), 16),
    b: parseInt(full.substring(4, 6), 16),
  };
}

function luminance({ r, g, b }) {
  const a = [r, g, b].map((v) => {
    v /= 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * a[0] + 0.7152 * a[1] + 0.0722 * a[2];
}

export function contrastRatio(fg, bg) {
  try {
    const l1 = luminance(hexToRgb(fg));
    const l2 = luminance(hexToRgb(bg));
    const lighter = Math.max(l1, l2);
    const darker = Math.min(l1, l2);
    return (lighter + 0.05) / (darker + 0.05);
  } catch {
    return 21;
  }
}

// ---------- Encoders ----------
export function escapeWifi(s) {
  return (s || "").replace(/([\\;,:"])/g, "\\$1");
}

export function buildQRValue(type, fields) {
  switch (type) {
    case "url":
    case "doi":
    case "article":
    case "survey":
    case "reference":
    case "pdf":
    case "orcid": {
      let v = (fields.value || "").trim();
      if (type === "doi" && v && !/^https?:\/\//i.test(v)) {
        v = "https://doi.org/" + v.replace(/^doi:/i, "");
      }
      if (type === "orcid" && v && !/^https?:\/\//i.test(v)) {
        v = "https://orcid.org/" + v.replace(/^orcid:\s*/i, "");
      }
      if (v && !/^(https?:|mailto:|tel:)/i.test(v) && /\./.test(v)) {
        v = "https://" + v;
      }
      return v;
    }
    case "email":
      return fields.value ? `mailto:${fields.value.trim()}` : "";
    case "phone":
      return fields.value ? `tel:${fields.value.trim()}` : "";
    case "text":
      return fields.value || "";
    case "wifi": {
      const { ssid, password, encryption = "WPA", hidden } = fields;
      if (!ssid) return "";
      return `WIFI:T:${encryption};S:${escapeWifi(ssid)};P:${escapeWifi(password || "")};H:${hidden ? "true" : "false"};;`;
    }
    case "vcard": {
      const { fullName, org, title, phone, email, website } = fields;
      if (!fullName) return "";
      const lines = [
        "BEGIN:VCARD",
        "VERSION:3.0",
        `N:${fullName}`,
        `FN:${fullName}`,
      ];
      if (org) lines.push(`ORG:${org}`);
      if (title) lines.push(`TITLE:${title}`);
      if (phone) lines.push(`TEL;TYPE=CELL:${phone}`);
      if (email) lines.push(`EMAIL:${email}`);
      if (website) lines.push(`URL:${website}`);
      lines.push("END:VCARD");
      return lines.join("\n");
    }
    default:
      return fields.value || "";
  }
}

// ---------- QR instance ----------
export function makeQR(options) {
  const {
    data,
    size = 320,
    fgColor = "#0A2540",
    bgColor = "#FFFFFF",
    dotType = "square",
    cornerType = "square",
    margin = 16,
    errorCorrectionLevel = "M",
    logo = null,
    transparent = false,
    type = "canvas",
  } = options;

  return new QRCodeStyling({
    width: size,
    height: size,
    type,
    data: data || " ",
    margin,
    qrOptions: { errorCorrectionLevel },
    image: logo || undefined,
    dotsOptions: { color: fgColor, type: dotType },
    cornersSquareOptions: { color: fgColor, type: cornerType },
    cornersDotOptions: { color: fgColor, type: cornerType === "square" ? "square" : "dot" },
    backgroundOptions: { color: transparent ? "transparent" : bgColor },
    imageOptions: { crossOrigin: "anonymous", margin: 6, imageSize: 0.28 },
  });
}

// ---------- Verify decode with jsQR ----------
// Verifies the ACTUAL artifact: respects the requested output size and margin
// (quiet zone), capping the render size for decode speed while preserving the
// margin ratio so verification reflects what is actually downloaded.
export async function verifyQR(expectedData, options) {
  try {
    const outSize = options.size || 512;
    const cap = 1024;
    const scale = outSize > cap ? cap / outSize : 1;
    const vSize = Math.round(outSize * scale);
    const vMargin = Math.round((options.margin ?? 16) * scale);
    const qr = makeQR({ ...options, data: expectedData, size: vSize, margin: vMargin, transparent: false, logo: null });
    const blob = await qr.getRawData("png");
    const bitmap = await createImageBitmap(blob);
    const canvas = document.createElement("canvas");
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(bitmap, 0, 0);
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const decoded = jsQR(imageData.data, imageData.width, imageData.height);
    if (!decoded) return { ok: false, reason: "Could not decode the generated QR code." };
    if (decoded.data !== expectedData)
      return { ok: false, reason: "Decoded QR data did not match the input." };
    return { ok: true };
  } catch (e) {
    return { ok: false, reason: "Verification failed: " + (e?.message || "unknown error") };
  }
}

// Scale a preview-space margin (measured at previewSize px) up to the output
// size so the quiet-zone RATIO is preserved, with a safe minimum of ~6%.
export function scaledQuietZone(previewMargin, previewSize, outSize) {
  const ratioBased = Math.round((previewMargin / previewSize) * outSize);
  const minZone = Math.round(outSize * 0.06);
  return Math.max(ratioBased, minZone);
}

